import { useTranslation } from 'react-i18next';
import type { ReplitAllergy } from '@/types/replit-dashboard';
import ExplainButton from './ExplainButton';

interface AllergiesListProps {
  allergies: ReplitAllergy[];
  patientFirstName: string;
  onAskQuestion: (question: string) => void;
}

export default function AllergiesList({ allergies, patientFirstName, onAskQuestion }: AllergiesListProps) {
  const { t } = useTranslation();

  if (!allergies || allergies.length === 0) {
    return <p className="text-sm text-gray-500">{t('dashboard.noKnownAllergies')}</p>;
  }

  return (
    <div className="space-y-3 text-sm">
      {allergies.slice(0, 5).map((allergy, index) => (
        <div key={allergy.id || index} className="border-l-4 border-red-500 p-3 bg-red-50 rounded-r-lg">
          <div className="flex items-start justify-between mb-2">
            <h4 className="font-semibold text-red-900 text-sm">{allergy.substance}</h4>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              allergy.criticality === 'high' ? 'bg-red-200 text-red-800' :
              allergy.criticality === 'low' ? 'bg-yellow-200 text-yellow-800' :
              'bg-gray-200 text-gray-800'
            }`}>
              {allergy.criticality || t('dashboard.unknownCriticality')}
            </span>
          </div>
          {allergy.reactions && allergy.reactions.length > 0 && (
            <div className="text-xs text-red-800 mb-1.5">
              <strong>{t('dashboard.reactionsLabel')}</strong> {allergy.reactions.map((r) => r.manifestation).join(', ')}
            </div>
          )}
          <div className="flex justify-end">
            <ExplainButton
              onClick={() => onAskQuestion(t('explain.allergy', { name: patientFirstName, substance: allergy.substance }))}
              ariaLabel={t('dashboard.explainAllergyAriaLabel', { substance: allergy.substance })}
              testId={`button-explain-allergy-${index}`}
            />
          </div>
        </div>
      ))}
      {allergies.length > 5 && (
        <p className="text-xs text-gray-500">{t('dashboard.moreAllergies', { count: allergies.length - 5 })}</p>
      )}
    </div>
  );
}
